/**
 * Internal Observatory evaluation contracts (R20–R22, KTD18, U16).
 *
 * Every problem reported from outside the observatory (a Linear defect, a
 * customer report, a Slack thread) is checked against it: was it found, how
 * early, and if missed, why. Three endpoints under
 * `/internal/observatory/signals` serve `$OBS signals check | eval | scorecard`
 * and `pnpm sj signals check | scorecard`:
 *
 *  - `GET check` ranks candidate Issues for one report. Read-only.
 *  - `POST eval` records a verdict (human-authority key only), `POST eval/propose`
 *    stores an agent's proposal (inert until acknowledged), `POST eval/ack`
 *    acknowledges one. A confirmed fixable miss files one gap ticket and
 *    returns a known-case fixture stub, which the CLI writes on the operator's
 *    machine; the server never writes a file.
 *  - `GET scorecard` computes R21 from acknowledged cases, human labels and
 *    filing and alert claims.
 *
 * The lead is the report time minus the Issue's first-seen (KTD18), never its
 * filing time, so detection and filing delays are measured apart.
 *
 * Plan: docs/plans/2026-09-26-1745-feat-observatory-signal-triage-pipeline-plan.md (U16)
 */

import { z } from 'zod'

import {
  ObservatoryActorIdSchema,
  ObservatoryEvalCaseSchema,
  ObservatoryEvalMissReasonSchema,
  ObservatoryEvalVerdictSchema,
  ObservatorySeveritySchema,
  ObservatorySignalSourceSchema,
  ObservatoryTicketStateSchema
} from '../../../schemas/entities/observatory-signal'

/** A Linear identifier such as SCH-2306: the one kind of external ref the backend can read itself. */
export const LINEAR_REPORT_REF_PATTERN = /^[A-Z][A-Z0-9]*-\d+$/

/** `60` or `60,61`, as a query value or a JSON array. */
const SchoolIdsQuerySchema = z.preprocess(
  value => (typeof value === 'string' ? value.split(',').filter(part => part.trim() !== '') : value),
  z.array(z.coerce.number().int().positive()).max(50)
)

/** A SCH key, Slack permalink or incident id. */
const ExternalRefSchema = z.string().trim().min(1).max(500)

/** What a report is, wherever it came from. Shared by `check` and `eval record`. */
const ReportInputShape = {
  /** A SCH key is read from Linear (title, description, created time, customer school). */
  ref: ExternalRefSchema.optional(),
  /** The report text; required when `ref` is not a Linear key. Overrides nothing Linear returns. */
  text: z.string().trim().min(1).max(4000).optional(),
  /** Schools the report is about, added to any Linear resolves. */
  school: SchoolIdsQuerySchema.optional(),
  /** When it was reported; required without a Linear key, and overrides Linear's created time. */
  at: z.iso.datetime().optional()
}

const reportIsResolvable = (q: { ref?: string; text?: string; at?: string }) =>
  (q.ref !== undefined && LINEAR_REPORT_REF_PATTERN.test(q.ref)) || (q.text !== undefined && q.at !== undefined)
const REPORT_REFINEMENT = {
  message: 'pass a Linear key as ref, or both text and at',
  path: ['ref']
}

// =============================================================================
// check
// =============================================================================

/** `GET /internal/observatory/signals/check`. */
export const ObservatoryEvalCheckQuerySchema = z
  .object({
    ...ReportInputShape,
    limit: z.coerce.number().int().min(1).max(20).optional().prefault(5)
  })
  .refine(reportIsResolvable, REPORT_REFINEMENT)
export type ObservatoryEvalCheckQuery = z.infer<typeof ObservatoryEvalCheckQuerySchema>

/** The report as the backend resolved it. */
export const ObservatoryEvalReportSchema = z.object({
  externalRef: z.string().nullable(),
  /** `linear` when read from a SCH key, `manual` when given as text. */
  origin: z.enum(['linear', 'manual']),
  title: z.string().nullable(),
  /** Title and description (or the given text), trimmed; what the matcher reads. */
  text: z.string(),
  reportedAt: z.iso.datetime(),
  schoolIds: z.array(z.number().int().positive()),
  /** The candidate window: 7 days before to 1 day after the report (KTD18). */
  window: z.object({ from: z.iso.datetime(), to: z.iso.datetime() })
})
export type ObservatoryEvalReport = z.infer<typeof ObservatoryEvalReportSchema>

/** One ranked Issue. `leadMinutes` is report time minus first-seen; negative means found after. */
export const ObservatoryEvalCandidateSchema = z.object({
  issueKey: z.string(),
  title: z.string(),
  score: z.number().nonnegative(),
  /** Why it ranked, e.g. `school 60`, `sync type SECTION_ROSTER`, `tokens: roster, failed`. */
  reasons: z.array(z.string()),
  firstSeenAt: z.iso.datetime(),
  leadMinutes: z.number().int(),
  /** The verdict the lead implies, if a human confirms this candidate. */
  impliedVerdict: z.enum(['found-before', 'found-after']),
  /** The highest severity any member signal had at report time; null when none was observed by then. */
  severityAtReport: ObservatorySeveritySchema.nullable(),
  severityNow: ObservatorySeveritySchema.nullable(),
  ticketRef: z.string().nullable(),
  /** Null when no ticket was filed by report time. */
  ticketStateAtReport: ObservatoryTicketStateSchema.nullable(),
  ticketStateNow: ObservatoryTicketStateSchema.nullable(),
  sources: z.array(ObservatorySignalSourceSchema),
  signalCount: z.number().int().nonnegative(),
  /** Every member signal in the window was suppressed: a `suppressed` miss, not a find. */
  suppressed: z.boolean()
})
export type ObservatoryEvalCandidate = z.infer<typeof ObservatoryEvalCandidateSchema>

export const ObservatoryEvalCheckResponseSchema = z.object({
  report: ObservatoryEvalReportSchema,
  /** Best first. Empty means nothing in the window matched: a `missed` or `not-observable` case. */
  candidates: z.array(ObservatoryEvalCandidateSchema),
  /** The case already recorded for this ref, if any. */
  existingCase: ObservatoryEvalCaseSchema.nullable(),
  /** True when the window held more signals than one store page (500); the ranking saw the newest. */
  truncated: z.boolean()
})
export type ObservatoryEvalCheckResponse = z.infer<typeof ObservatoryEvalCheckResponseSchema>

// =============================================================================
// eval record / propose / ack
// =============================================================================

/**
 * `POST /internal/observatory/signals/eval` (human-authority key) and
 * `POST /internal/observatory/signals/eval/propose` (shared key). The backend
 * re-reads the report and the Issue and computes the lead, the severity and the
 * ticket state at report time itself. With `issueKey` and no `verdict`, the
 * verdict follows the lead's sign.
 */
export const RecordObservatoryEvalCaseRequestSchema = z
  .object({
    ...ReportInputShape,
    ref: ExternalRefSchema,
    issueKey: z.string().min(1).max(191).nullable().optional().default(null),
    verdict: ObservatoryEvalVerdictSchema.optional(),
    missReason: ObservatoryEvalMissReasonSchema.nullable().optional().default(null),
    actorId: ObservatoryActorIdSchema
  })
  .refine(reportIsResolvable, REPORT_REFINEMENT)
export type RecordObservatoryEvalCaseRequest = z.infer<typeof RecordObservatoryEvalCaseRequestSchema>

/** `POST /internal/observatory/signals/eval/ack` — human-authority key only. The ref rides in the body: a permalink holds `/`. */
export const AckObservatoryEvalCaseRequestSchema = z.object({
  ref: ExternalRefSchema,
  acknowledgedBy: ObservatoryActorIdSchema
})
export type AckObservatoryEvalCaseRequest = z.infer<typeof AckObservatoryEvalCaseRequestSchema>

/**
 * What happened to the gap ticket (R22). `filed` is new, `existing` was found
 * on the case or by its marker, `not-needed` is a find, `other` reason or an
 * unacknowledged proposal. `labelApplied: false` means the `observatory-gap`
 * label does not exist in Linear and the title carries the prefix instead.
 */
export const ObservatoryEvalGapSchema = z.object({
  status: z.enum(['filed', 'existing', 'not-needed']),
  ticketRef: z.string().nullable(),
  labelApplied: z.boolean().nullable()
})
export type ObservatoryEvalGap = z.infer<typeof ObservatoryEvalGapSchema>

/**
 * The known-case fixture stub the CLI writes (R22), relative to the repo root.
 * The gap's PR fills in the evidence. Null when no gap is owed.
 */
export const ObservatoryEvalFixtureSchema = z.object({
  path: z.string(),
  content: z.record(z.string(), z.unknown())
})
export type ObservatoryEvalFixture = z.infer<typeof ObservatoryEvalFixtureSchema>

export const RecordObservatoryEvalCaseResponseSchema = z.object({
  case: ObservatoryEvalCaseSchema,
  gap: ObservatoryEvalGapSchema,
  fixture: ObservatoryEvalFixtureSchema.nullable()
})
export type RecordObservatoryEvalCaseResponse = z.infer<typeof RecordObservatoryEvalCaseResponseSchema>

/** `GET /internal/observatory/signals/eval` — the cases, newest report first. */
export const ListObservatoryEvalCasesQuerySchema = z.object({
  /** Only unacknowledged agent proposals. */
  pending: z.stringbool().optional().default(false),
  since: z.iso.datetime().optional(),
  limit: z.coerce.number().int().min(1).max(1000).optional().prefault(100)
})
export type ListObservatoryEvalCasesQuery = z.infer<typeof ListObservatoryEvalCasesQuerySchema>

export const ListObservatoryEvalCasesResponseSchema = z.object({
  cases: z.array(ObservatoryEvalCaseSchema)
})
export type ListObservatoryEvalCasesResponse = z.infer<typeof ListObservatoryEvalCasesResponseSchema>

// =============================================================================
// scorecard
// =============================================================================

/** `GET /internal/observatory/signals/scorecard`. */
export const ObservatoryScorecardQuerySchema = z.object({
  weeks: z.coerce.number().int().min(1).max(52).optional().prefault(4)
})
export type ObservatoryScorecardQuery = z.infer<typeof ObservatoryScorecardQuerySchema>

/** Median and p90 of a set of durations, in minutes; null values when the set is empty. */
export const ObservatoryDurationStatsSchema = z.object({
  n: z.number().int().nonnegative(),
  medianMinutes: z.number().nullable(),
  p90Minutes: z.number().nullable()
})
export type ObservatoryDurationStats = z.infer<typeof ObservatoryDurationStatsSchema>

/**
 * R21. Every rate is null when its denominator is 0, never 0 or 1 by default.
 *
 *  - `recall.beforeReport`: found-before over every case except `not-observable`.
 *    `recall.any` also counts found-after.
 *  - `lead`: over every found case (a found-after lead is negative, and counts).
 *  - `firstSeenToTicket` / `firstSeenToAlert`: episode start to the filing or
 *    critical-alert claim, for claims on the Issue's current episode.
 *  - `precision`: filed Issues a human judged real over those a human judged at
 *    all. A human `state=suppressed` label on the Issue (including the implicit
 *    one a human cancel writes) is noise; any other human label is real.
 */
export const ObservatoryScorecardSchema = z.object({
  since: z.iso.datetime(),
  weeks: z.number().int().min(1),
  cases: z.object({
    total: z.number().int().nonnegative(),
    observable: z.number().int().nonnegative(),
    byVerdict: z.record(ObservatoryEvalVerdictSchema, z.number().int().nonnegative())
  }),
  recall: z.object({ beforeReport: z.number().nullable(), any: z.number().nullable() }),
  lead: ObservatoryDurationStatsSchema,
  firstSeenToTicket: ObservatoryDurationStatsSchema,
  firstSeenToAlert: ObservatoryDurationStatsSchema,
  precision: z.object({
    value: z.number().nullable(),
    filed: z.number().int().nonnegative(),
    judgedReal: z.number().int().nonnegative(),
    judgedNoise: z.number().int().nonnegative()
  }),
  missesByReason: z.partialRecord(ObservatoryEvalMissReasonSchema, z.number().int().nonnegative())
})
export type ObservatoryScorecard = z.infer<typeof ObservatoryScorecardSchema>
